import type { TaskOperatorReadModel } from "@/hooks/use-tasks";
import { formatMonthDay } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type CrmTaskAutomationResolution = TaskOperatorReadModel["automationResolution"];
type CrmTaskAutomationPolicy = TaskOperatorReadModel["automationPolicy"];
type CrmTaskSla = TaskOperatorReadModel["sla"];
type CrmTaskEscalation = TaskOperatorReadModel["escalation"];

export type CrmTaskSlaTone = "default" | "warning" | "critical" | "info";

interface CrmTaskAutomationStatusCardProps {
  automationResolution: CrmTaskAutomationResolution | null | undefined;
  automationPolicy: CrmTaskAutomationPolicy | null | undefined;
  isClosed: boolean;
}

interface CrmTaskSlaEscalationPanelProps {
  sla: CrmTaskSla | null | undefined;
  escalation: CrmTaskEscalation | null | undefined;
}

export function CrmTaskAutomationStatusCard({
  automationResolution,
  automationPolicy,
  isClosed,
}: CrmTaskAutomationStatusCardProps) {
  if (automationResolution) {
    return (
      <div className="mb-3 rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-3">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-success)]">
          {automationResolution.title}
        </p>
        <p className="mt-1 text-sm text-[var(--color-foreground)]">{automationResolution.reason}</p>
        {(automationResolution.eventType || automationResolution.eventId) && (
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {automationResolution.eventType ? `Event: ${automationResolution.eventType}` : null}
            {automationResolution.eventType && automationResolution.eventId ? " · " : null}
            {automationResolution.eventId ? `ID: ${automationResolution.eventId}` : null}
          </p>
        )}
      </div>
    );
  }

  if (isClosed || !automationPolicy) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)]">
        {automationPolicy.title}
      </p>
      <p className="mt-1 text-sm text-[var(--color-foreground)]">{automationPolicy.summary}</p>
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        {automationPolicy.autoCompleteEvents.length > 0 && (
          <CrmTaskAutomationEventList
            title="Auto-completes on"
            eventKeyPrefix="complete"
            events={automationPolicy.autoCompleteEvents}
          />
        )}
        {automationPolicy.autoCancelEvents.length > 0 && (
          <CrmTaskAutomationEventList
            title="Auto-cancels on"
            eventKeyPrefix="cancel"
            events={automationPolicy.autoCancelEvents}
          />
        )}
      </div>
      {automationPolicy.conditionalEvents.length > 0 && (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Payload-qualified rules: {automationPolicy.conditionalEvents.join(", ")}
        </p>
      )}
    </div>
  );
}

function CrmTaskAutomationEventList({
  title,
  eventKeyPrefix,
  events,
}: {
  title: string;
  eventKeyPrefix: string;
  events: string[];
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        {title}
      </p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {events.map((eventType) => (
          <span
            key={`${eventKeyPrefix}:${eventType}`}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-[11px] text-[var(--color-muted-foreground)]"
          >
            {eventType}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CrmTaskSlaEscalationPanel({ sla, escalation }: CrmTaskSlaEscalationPanelProps) {
  if (!sla && !escalation) return null;

  const slaDetail = sla ? getCrmTaskSlaDetail(sla) : null;

  return (
    <div className="mb-3 grid gap-3 md:grid-cols-2">
      {sla && (
        <div className={cn(
          "rounded-md border p-3",
          getCrmTaskSlaTone(sla) === "critical"
            ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10"
            : getCrmTaskSlaTone(sla) === "warning"
              ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10"
              : "border-[var(--color-border)] bg-[var(--color-card)]",
        )}>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Deadline / SLA
          </p>
          <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{sla.label}</p>
          {slaDetail && (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{slaDetail}</p>
          )}
          {sla.policyId && (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Policy: {sla.policyId}</p>
          )}
        </div>
      )}
      {escalation && (
        <div className="rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-warning)]">
            Escalation
          </p>
          <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">
            {escalation.state === "escalated" ? "Escalated" : "Escalation candidate"}
            {escalation.level ? ` · ${escalation.level}` : ""}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{escalation.reason}</p>
          {(escalation.target || escalation.escalatedAt) && (
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {escalation.target ? `Target: ${escalation.target}` : null}
              {escalation.target && escalation.escalatedAt ? " · " : null}
              {escalation.escalatedAt ? `At: ${formatMonthDay(escalation.escalatedAt)}` : null}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function getCrmTaskSlaTone(sla: CrmTaskSla): CrmTaskSlaTone {
  if (sla.severity === "critical") return "critical";
  if (sla.severity === "warning") return "warning";
  if (sla.severity === "info") return "info";
  return "default";
}

export function getCrmTaskSlaDetail(sla: CrmTaskSla): string | null {
  const delta = formatCrmTaskSlaMinutes(sla.minutesUntilDue);
  if (sla.status === "overdue" && delta) return `${delta} overdue`;
  if (sla.status === "due_soon" && delta) return `Due in ${delta}`;
  if (sla.status === "scheduled" && delta) return `Due in ${delta}`;
  if (sla.status === "closed") return "Closed task";
  return sla.dueAt ? formatMonthDay(sla.dueAt) : null;
}

function formatCrmTaskSlaMinutes(value: number | null): string | null {
  if (value === null) return null;
  const abs = Math.abs(value);
  if (abs < 60) return `${abs}m`;
  const hours = Math.round(abs / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}
